'use client';
import * as React from 'react';
import styles from './HabitSelector.module.scss';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../api/ApiClientContext';
import Spinner from '../spinner/Spinner';
import Button from '../button/Button';
import { getHabitImageUrl } from '../../utils/habitImages';
import { Icon } from '../../assets/icons/Icon';

export interface Habit {
  id: string;
  name: string;
  description: string;
  instructions: string;
  difficulty: string;
  estimated_time_minutes: number | null;
  default_duration_days: number;
  tips: string | null;
  icon_url: string | null;
}

export interface BehaviorSection {
  id: string;
  name: string;
  description: string;
  sequence_order: number;
  habits: Habit[];
}

export interface HabitSelectorProps {
  selectedHabits?: string[];
  setSelectedHabits?: React.Dispatch<React.SetStateAction<string[]>>;
  onStartTracking?: (selectedHabits: string[]) => void;
  isOnboarding?: boolean;
  onClose?: () => void;
  behaviourFilter?: string;
  programmeId?: string;
  moduleId?: string;
}

export function HabitSelector({
  selectedHabits: controlledSelectedHabits,
  setSelectedHabits: setControlledSelectedHabits,
  onStartTracking,
  onClose,
  isOnboarding,
  behaviourFilter,
  programmeId: propProgrammeId,
  moduleId: propModuleId,
}: HabitSelectorProps) {
  const [uncontrolledSelectedHabits, setUncontrolledSelectedHabits] = React.useState<string[]>([]);
  const [expandedSections, setExpandedSections] = React.useState<string[]>([]);
  const selectedHabits = controlledSelectedHabits ?? uncontrolledSelectedHabits;
  const setSelectedHabits = setControlledSelectedHabits ?? setUncontrolledSelectedHabits;

  const queryClient = useQueryClient();
  const maxSelections = 3;
  const apiClient = useApiClient();

  const { mutate: selectHabits, isPending } = useMutation({
    mutationFn: async (habitId: string) => {
      return apiClient.api.selectHabitApiV1HabitsSelectPost({
        habit_id: habitId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userFlowStatus'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['programmes'] });
      onClose?.();
    },
  });

  // When no explicit programmeId prop, fetch active programmes and use the first one
  const { data: activeProgrammes } = useQuery({
    queryKey: ['programmes/active-list'],
    enabled: !propProgrammeId,
    queryFn: async () => {
      const res = await apiClient.api.getUserActiveProgrammesApiV1ProgrammesActiveGet();
      return res?.data;
    },
  });

  const effectiveProgrammeId = propProgrammeId || activeProgrammes?.[0]?.id;
  const effectiveModuleId = propModuleId || activeProgrammes?.[0]?.current_module_id;

  const { data: activeHabits } = useQuery({
    queryKey: ['myActiveHabits'],
    queryFn: async () => {
      const res = await apiClient.api.getMyActiveHabitsApiV1HabitsMyActiveGet();
      return res?.data;
    },
  });

  const {
    data: programmeBehaviours,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['programmeBehaviours', effectiveProgrammeId, effectiveModuleId],
    enabled: !!effectiveProgrammeId,
    queryFn: async () => {
      const res =
        await apiClient.api.getProgrammeBehavioursWithHabitsApiV1ProgrammesProgrammeIdBehavioursGet(
          effectiveProgrammeId!,
          effectiveModuleId ? { module_id: effectiveModuleId } : {},
        );
      return res?.data;
    },
  });

  const handleHabitToggle = (habitId: string) => {
    setSelectedHabits((prev = []) => {
      let newSelection;
      if (prev.includes(habitId)) {
        newSelection = prev.filter((id) => id !== habitId);
      } else if (prev.length < maxSelections) {
        newSelection = [...prev, habitId];
      } else {
        return prev;
      }
      return newSelection;
    });
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId) ? prev.filter((id) => id !== sectionId) : [...prev, sectionId],
    );
  };

  if (isLoading || !programmeBehaviours) {
    return <Spinner />;
  }

  const activeHabitIds = Array.isArray(activeHabits)
    ? activeHabits.map((h: any) => h.habit.id)
    : [];

  const sortedBehaviors: BehaviorSection[] = Array.isArray(programmeBehaviours)
    ? programmeBehaviours
        .filter((b) => !behaviourFilter || b.id === behaviourFilter) // Filter by behaviour if specified
        .map((b) => ({
          ...b,
          description: b.description ?? '',
          habits: Array.isArray(b.habits)
            ? (b.habits as Habit[]).filter((habit) => !activeHabitIds.includes(habit.id))
            : [],
        }))
        .sort((a, b) => a.sequence_order - b.sequence_order)
    : [];

  const handleStartTracking = () => {
    if (onStartTracking) {
      onStartTracking(selectedHabits);
    } else {
      selectedHabits?.forEach((habitId) => {
        selectHabits(habitId);
      });
    }
  };


  return (
    <div className={styles.container}>
      <div className={styles.wrapper}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            Please select up to {maxSelections} habits from any of the following behaviours
          </div>
          <div className={styles.selectionCounter}>
            Selected {selectedHabits?.length ?? 0}/{maxSelections}
          </div>
        </div>

        {sortedBehaviors.map((behavior) => {
          const isExpanded = expandedSections.includes(behavior.id);

          return (
            <div key={behavior.id}>
              <div
                className={styles.behaviorSection}
                onClick={() => toggleSection(behavior.id)}
                tabIndex={0}
                role="button"
                aria-expanded={isExpanded}
              >
                <div className={styles.behaviorInfo}>
                  <div className={styles.behaviorTitle}>{behavior.name}</div>
                  <div className={styles.behaviorSubtitle}>
                    {behavior.habits.length} habit{behavior.habits.length !== 1 ? 's' : ''}
                  </div>
                </div>
                {isExpanded ?  <Icon iconName="ArrowDownIcon"/> : <Icon iconName="ArrowUpIcon"/>}


              </div>

              <div className={`${styles.habitsContainer} ${!isExpanded ? styles.collapsed : ''}`}>
                {behavior.habits.map((habit) => (
                  <div
                    key={habit.id}
                    className={`${styles.habitCard} ${
                      selectedHabits?.includes(habit.id) ? styles.selected : ''
                    }`}
                    onClick={() => handleHabitToggle(habit.id)}
                    tabIndex={0}
                    role="button"
                    aria-pressed={selectedHabits?.includes(habit.id)}
                  >
                    <img
                      src={getHabitImageUrl(habit.icon_url, habit.id)}
                      alt={habit.name}
                    />
                    <div className={styles.habitContent}>
                      <div className={styles.habitTitle}>{habit.name}</div>
                      <div className={styles.habitDescription}>{habit.description}</div>
                      {habit.instructions && (
                        <div className={styles.habitInstructions}>
                          <strong>Instructions:</strong> {habit.instructions}
                        </div>
                      )}
                      <div className={styles.habitMeta}>
                        <span className={styles.difficulty}>Difficulty: {habit.difficulty}</span>
                        {habit.estimated_time_minutes && (
                          <span className={styles.timeEstimate}>
                            {habit.estimated_time_minutes} min
                          </span>
                        )}
                        <span className={styles.duration}>{habit.default_duration_days} days</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {!isOnboarding && (
          <Button className={styles.saveBtn} onClick={handleStartTracking}>
            {isPending ? 'Saving..' : 'Save'}
          </Button>
        )}
      </div>
    </div>
  );
}
